/**
 * Depreciation calculation utilities
 * Supports multiple depreciation methods for asset accounting
 */

export type DepreciationMethod = 'straight-line' | 'declining-balance' | 'double-declining' | 'sum-of-years';

export interface DepreciationConfig {
  depreciableCost: number;
  salvageValue: number;
  assetLifeMonths: number;
  dateAcquired: string;
  method: DepreciationMethod;
}

export interface DepreciationResult {
  method: DepreciationMethod;
  totalDepreciation: number;
  currentBookValue: number;
  accumulatedDepreciation: number;
  remainingValue: number;
  monthlyDepreciation: number;
  yearsElapsed: number;
  monthsElapsed: number;
  percentDepreciated: number;
}

/**
 * Calculate the number of months between two dates
 */
function getMonthsElapsed(startDate: string, endDate: Date = new Date()): number {
  const start = new Date(startDate);
  const monthsDiff =
    (endDate.getFullYear() - start.getFullYear()) * 12 +
    (endDate.getMonth() - start.getMonth());

  return Math.max(0, monthsDiff);
}

/**
 * Straight-line depreciation
 * Formula: (Cost - Salvage Value) / Useful Life
 */
function calculateStraightLine(config: DepreciationConfig): DepreciationResult {
  const { depreciableCost, salvageValue, assetLifeMonths, dateAcquired } = config;

  const monthsElapsed = getMonthsElapsed(dateAcquired);
  const monthsToDepreciate = Math.min(monthsElapsed, assetLifeMonths);

  const totalDepreciableAmount = depreciableCost - salvageValue;
  const monthlyDepreciation = totalDepreciableAmount / assetLifeMonths;
  const accumulatedDepreciation = monthlyDepreciation * monthsToDepreciate;
  const currentBookValue = depreciableCost - accumulatedDepreciation;
  const remainingValue = Math.max(currentBookValue, salvageValue);

  return {
    method: 'straight-line',
    totalDepreciation: totalDepreciableAmount,
    currentBookValue: remainingValue,
    accumulatedDepreciation,
    remainingValue: remainingValue - salvageValue,
    monthlyDepreciation,
    yearsElapsed: monthsElapsed / 12,
    monthsElapsed,
    percentDepreciated: (accumulatedDepreciation / totalDepreciableAmount) * 100,
  };
}

/**
 * Declining balance depreciation
 * Formula: Book Value * (Depreciation Rate / 12)
 * Rate = 1 / Useful Life
 */
function calculateDecliningBalance(config: DepreciationConfig): DepreciationResult {
  const { depreciableCost, salvageValue, assetLifeMonths, dateAcquired } = config;

  const monthsElapsed = getMonthsElapsed(dateAcquired);
  const monthsToDepreciate = Math.min(monthsElapsed, assetLifeMonths);

  const annualRate = 1 / (assetLifeMonths / 12);
  const monthlyRate = annualRate / 12;

  let bookValue = depreciableCost;
  let accumulatedDepreciation = 0;

  for (let month = 0; month < monthsToDepreciate; month++) {
    const monthlyDepreciation = bookValue * monthlyRate;

    // Don't depreciate below salvage value
    if (bookValue - monthlyDepreciation < salvageValue) {
      accumulatedDepreciation += bookValue - salvageValue;
      bookValue = salvageValue;
      break;
    }

    accumulatedDepreciation += monthlyDepreciation;
    bookValue -= monthlyDepreciation;
  }

  const totalDepreciableAmount = depreciableCost - salvageValue;
  const currentMonthlyDepreciation = bookValue > salvageValue ? bookValue * monthlyRate : 0;

  return {
    method: 'declining-balance',
    totalDepreciation: totalDepreciableAmount,
    currentBookValue: bookValue,
    accumulatedDepreciation,
    remainingValue: bookValue - salvageValue,
    monthlyDepreciation: currentMonthlyDepreciation,
    yearsElapsed: monthsElapsed / 12,
    monthsElapsed,
    percentDepreciated: (accumulatedDepreciation / totalDepreciableAmount) * 100,
  };
}

/**
 * Double declining balance depreciation
 * Formula: Book Value * (2 / Useful Life / 12)
 */
function calculateDoubleDeclining(config: DepreciationConfig): DepreciationResult {
  const { depreciableCost, salvageValue, assetLifeMonths, dateAcquired } = config;

  const monthsElapsed = getMonthsElapsed(dateAcquired);
  const monthsToDepreciate = Math.min(monthsElapsed, assetLifeMonths);

  const annualRate = 2 / (assetLifeMonths / 12);
  const monthlyRate = annualRate / 12;

  let bookValue = depreciableCost;
  let accumulatedDepreciation = 0;

  for (let month = 0; month < monthsToDepreciate; month++) {
    const monthlyDepreciation = bookValue * monthlyRate;

    // Don't depreciate below salvage value
    if (bookValue - monthlyDepreciation < salvageValue) {
      accumulatedDepreciation += bookValue - salvageValue;
      bookValue = salvageValue;
      break;
    }

    accumulatedDepreciation += monthlyDepreciation;
    bookValue -= monthlyDepreciation;
  }

  const totalDepreciableAmount = depreciableCost - salvageValue;
  const currentMonthlyDepreciation = bookValue > salvageValue ? bookValue * monthlyRate : 0;

  return {
    method: 'double-declining',
    totalDepreciation: totalDepreciableAmount,
    currentBookValue: bookValue,
    accumulatedDepreciation,
    remainingValue: bookValue - salvageValue,
    monthlyDepreciation: currentMonthlyDepreciation,
    yearsElapsed: monthsElapsed / 12,
    monthsElapsed,
    percentDepreciated: (accumulatedDepreciation / totalDepreciableAmount) * 100,
  };
}

/**
 * Sum of years digits depreciation
 * Formula: (Remaining Life / Sum of Years Digits) * Depreciable Amount
 */
function calculateSumOfYears(config: DepreciationConfig): DepreciationResult {
  const { depreciableCost, salvageValue, assetLifeMonths, dateAcquired } = config;

  const monthsElapsed = getMonthsElapsed(dateAcquired);
  const monthsToDepreciate = Math.min(monthsElapsed, assetLifeMonths);

  const assetLifeYears = assetLifeMonths / 12;
  const sumOfYears = (assetLifeYears * (assetLifeYears + 1)) / 2;
  const totalDepreciableAmount = depreciableCost - salvageValue;

  let accumulatedDepreciation = 0;

  for (let month = 1; month <= monthsToDepreciate; month++) {
    const yearsRemaining = assetLifeYears - ((month - 1) / 12);
    const fraction = yearsRemaining / sumOfYears;
    const monthlyDepreciation = (totalDepreciableAmount * fraction) / 12;

    accumulatedDepreciation += monthlyDepreciation;
  }

  // Calculate current month's depreciation
  const currentYearsRemaining = assetLifeYears - (monthsToDepreciate / 12);
  const currentFraction = Math.max(0, currentYearsRemaining) / sumOfYears;
  const currentMonthlyDepreciation = currentYearsRemaining > 0
    ? (totalDepreciableAmount * currentFraction) / 12
    : 0;

  const bookValue = depreciableCost - accumulatedDepreciation;

  return {
    method: 'sum-of-years',
    totalDepreciation: totalDepreciableAmount,
    currentBookValue: Math.max(bookValue, salvageValue),
    accumulatedDepreciation,
    remainingValue: Math.max(bookValue - salvageValue, 0),
    monthlyDepreciation: currentMonthlyDepreciation,
    yearsElapsed: monthsElapsed / 12,
    monthsElapsed,
    percentDepreciated: (accumulatedDepreciation / totalDepreciableAmount) * 100,
  };
}

/**
 * Calculate depreciation using the specified method
 */
export function calculateDepreciation(config: DepreciationConfig): DepreciationResult {
  switch (config.method) {
    case 'straight-line':
      return calculateStraightLine(config);
    case 'declining-balance':
      return calculateDecliningBalance(config);
    case 'double-declining':
      return calculateDoubleDeclining(config);
    case 'sum-of-years':
      return calculateSumOfYears(config);
    default:
      return calculateStraightLine(config);
  }
}

/**
 * Format currency for display
 */
export function formatDepreciationCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format percentage for display
 */
export function formatDepreciationPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Get depreciation method display name
 */
export function getDepreciationMethodName(method: DepreciationMethod): string {
  const names: Record<DepreciationMethod, string> = {
    'straight-line': 'Straight-Line',
    'declining-balance': 'Declining Balance',
    'double-declining': 'Double Declining Balance',
    'sum-of-years': 'Sum of Years Digits',
  };

  return names[method] || 'Straight-Line';
}
